<template>
  <div class="flex gap-4 py-8 px-1" :class="{'bg-jade-300/10 border border-jade-300 rounded-xl my-4 px-2 !py-5': props.highlight}">
    <template v-if="showPreviewImage">
      <div :style="previewImage" class="h-10 aspect-square bg-contain rounded-lg"></div>
    </template>
    <div class="grow">
      <template v-if="!isBonusReport">
        <div class="flex justify-between">
          <h3 @click="openContent" class="cursor-pointer mb-0">{{ title }}</h3>
          <div v-if="isAuthenticatedArticleOrWebinar" @click.prevent="handleFavoritedContentAction( content?.contentfulId, contentType )" class="text-jade tooltip" :data-tip="dataTipForFavoritedStatus( content?.contentfulId, contentType )">
            <i class="pi" :class="favoriteIconClassNameForContent( content?.contentfulId, contentType )"></i>
          </div>
        </div>
        <div class="text-sm mt-1">
          <template v-if="showAuthor">By <router-link :to="authorRoute" class="text-base-content"> {{ authorName }} </router-link></template>
          <template v-if="showProductTitle">
            <span>&nbsp;in&nbsp;</span>
            <router-link v-if="isProductDisplayable" :to="productRoute" class="text-base-content">
              {{ selectedOrPrimaryProduct?.name }}
            </router-link>
            <span v-else class="text-base-content">
              {{ selectedOrPrimaryProduct?.name }}
            </span>
          </template>
        </div>
        <div class="mt-4 text-sm">
          <div v-if="previewText" @click="openContent" class="cursor-pointer">{{ previewText }}</div>
          <div v-if="showTags">
            <Tags :tags-list="tagOptions" :slug="slug" />
          </div>
          <div class="flex items-end justify-between">
            <div v-if="!!estimatedReadingTime" class="text-zinc-500">
              {{ publishedDate }} | {{ estimatedReadingTime }}
            </div>
            <div @click="openContent" class="btn btn-sm btn-primary">Read more</div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex justify-between">
          <div>
            <h3 @click.prevent="openBonusReport()" class="cursor-pointer mb-0">{{ title }}</h3>
            <div class="text-sm mt-1">
              <template v-if="showAuthor">by <router-link :to="authorRoute" class="text-base-content"> {{ authorName }} </router-link></template>
              <template v-if="showProductTitle"> in <router-link :to="productRoute" class="text-base-content"> {{ selectedOrPrimaryProduct?.name }} </router-link></template>
            </div>
          </div>
          <div @click.prevent="openBonusReport()" class="btn btn-primary">Get Report</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";

import * as tmi from "@/constants";

import Tags from '@/components/content/Tags.vue';

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";

import { useContentActions } from "@/composables/content-actions";

import routeManager from "@/router/route-manager";

import { type Author, ContentfulItem, type HasAuthor, type Product, type Tag } from "@/types/entities";
import type { SearchContextWrapper } from "@/types/context/search-context";

import { articleKey, bonusReportKey, webinarKey } from "@/types/products/product-definitions";
import { reportContentViewEvent } from "@/composables/user-activity-monitor";
import { countTime, formatDateAsMonthDayYearString } from "@/helpers/dateTimeUtilities";

const route = useRoute();
const props = defineProps< { slug: string, contentType: string, highlight?: boolean } >();
const { isAuthenticated } = storeToRefs( useAuthStore() );

const injectedContext = inject< SearchContextWrapper | undefined >( tmi.searchContextSymbol );
if ( !injectedContext ) {
  throw new Error( `The expected provided value for 'searchContextSymbol' does not exist in this context.` );
}

const { contentBySlug, setSelectedContent } = injectedContext!;
const userStore = useUserStore();

const content = ref< ContentfulItem | undefined >( contentBySlug( props.slug ) );
const authoredContent = ref< HasAuthor | undefined >( content.value as HasAuthor ? content.value as HasAuthor : undefined );
const contentModelType = ref< string | undefined >( props.contentType );
const contentText = computed< string >( () => ( authoredContent.value?.text ?? '' ).trim() );

const isAuthenticatedArticleOrWebinar = computed< boolean >( () => {
  const authenticated = isAuthenticated.value
  const isArticleOrWebinar = ( !!contentModelType.value &&
      ( contentModelType.value.includes( articleKey ) || contentModelType.value.includes( webinarKey ) ) );

  const canBeFavorited = isFavoritable( content.value );

  return authenticated && isArticleOrWebinar && canBeFavorited;
} );

const isBonusReport = computed< boolean >( () => contentModelType.value === bonusReportKey );

const productSlug = computed< string | undefined >( () => route.params.productSlug ? `${ route.params.productSlug }` : undefined );
const showProductTitle = computed< boolean >( () => !!( isAuthenticated.value && selectedOrPrimaryProduct.value && title.value ) );

const selectedOrPrimaryProduct = computed< Product | undefined >( () => productSlug.value ? content.value?.products.find( ( p: Product ) => p.slug === productSlug.value ) : content.value?.products[ 0 ] );
const productRoute = computed< string >( () => !!selectedOrPrimaryProduct ? routeManager.productRouteBySlug( selectedOrPrimaryProduct.value!.slug ) : '#' );
const isProductDisplayable = computed< boolean | undefined >( () => selectedOrPrimaryProduct.value?.isDisplay );

const showAuthor = computed< boolean >( () => !!( author.value ) );
const author = computed< Author | null | undefined >( () => authoredContent.value?.author ?? undefined );
const authorName = computed< string >( () => author.value?.name ?? '---' );
const authorRoute = computed< string >( () => !!( author.value?.slug ) ? `${ tmi.AuthorsPath }/${ author.value?.slug }` : '#' );

const previewText = computed< string >( () => {
  // Remove any HTML tags within the main content and display the first 255 characters
  const domElement = document.createElement( 'div' );
  domElement.innerHTML = contentText.value;
  const trimmedString = `${ domElement.innerText.slice( 0, 200 ).trim() }`;
  return !!( trimmedString ) ? `${ trimmedString }...` : '';
} );

const title = computed< string | undefined >( () => content.value?.title );
const slug = computed< string | undefined >( () => content.value?.slug );

const publishedDate = ref< string >( formatDateAsMonthDayYearString( content.value?.publishedDate ) );
const estimatedReadingTime = computed< string >( () => countTime( authoredContent.value?.text ?? '' ) );

const previewImage = computed( () => `background-image: url(${ favicon.value })` );
const favicon = computed< string | undefined >( () => selectedOrPrimaryProduct.value?.favicon );
const showPreviewImage = computed< boolean >( () => !!favicon );

const tagOptions: Tag[] = [ ...( content.value?.tags ?? [] ) ].map( ( tag: Tag ) => ( { ...tag, label: tag.slug } ) );
const showTags = ref< boolean >( tagOptions.length > 0 );

function openContent() {
  if ( content.value?.contentfulId ) {
    setSelectedContent( content.value!.contentfulId );
  }
}

function openBonusReport() {
  handleOpenContentInNewTab( content.value, props.contentType );
  reportContentViewEvent( content.value, props.contentType );
}

const {
  dataTipForFavoritedStatus,
  favoriteIconClassNameForContent,
  handleFavoritedContentAction,
  handleOpenContentInNewTab,
  isFavoritable,
} = useContentActions();
</script>
